import { Link, Navigate, NavLink, useLocation, useParams } from 'react-router-dom';
import type { ReactNode } from 'react';
import { BackControl } from '../components/BackControl.tsx';
import { useClientBanner, useMe } from '../api/queries.ts';
import { LoadFailure, isSessionFailure } from '../components/LoadFailure.tsx';
import { splitByBand } from '../components/ClientHeader.tsx';
import { CriticalFlags } from '../components/FlagBanner.tsx';
import { ClientBar } from '../components/ClientBar.tsx';
import { Panel } from '../components/Panel.tsx';
import { ApiError } from '../api/client.ts';
import { useMySections, type SectionKey } from '../api/navigation.ts';
import { ClientDashboardSection } from './client/DashboardSection.tsx';
import { TimelineSection } from './client/TimelineSection.tsx';
import { PropertyPage } from './client/PropertyPage.tsx';
import { EditDetails, EditPeople } from './client/EditDetails.tsx';
import { SectionAction } from '../components/tasks/SectionAction.tsx';
import {
  CommunicationsSection, DocumentsSection, FinancialsSection, LegalSection, PropertySection, ReportsSection,
  SwSection, TasksSection,
} from './client/Sections.tsx';
import { TimeSection } from './client/TimeSection.tsx';

/**
 * A client's record (docs/navigation-brief.md, sections 5 and 6): the Critical
 * band full-bleed above everything, a sidebar of the nine sections the
 * reader's roles cover, and the section itself.
 *
 * The sidebar is drawn from GET /me/sections, the rule the API refuses with
 * (api/src/sections.ts). A section the reader may not see is not drawn, and
 * its routes refuse it anyway. Reserved sections are drawn, as plainly
 * labelled space for what is coming.
 */

export const SECTION_LABELS: Record<SectionKey, string> = {
  dashboard: 'Dashboard',
  sw: 'Social Work',
  financials: 'Finance',
  legal: 'Legal',
  reports: 'Reports',
  property: 'Property',
  documents: 'Documents',
  tasks: 'Tasks',
  time: 'Time',
  communications: 'Communications',
};

/** Sections with nothing behind them yet: drawn, labelled, never broken-looking. */
export const RESERVED: SectionKey[] = [];   // Communications was the last, until 138 (20 October)

/** Arthur's order and labels (14 October). */
const ORDER: SectionKey[] = [
  'dashboard', 'sw', 'financials', 'legal', 'reports', 'property', 'documents', 'tasks', 'time', 'communications',
];

/**
 * Each section's address. The two whose label changed carry the label in the
 * URL now; their old addresses (/sw, /financials) redirect, so a link kept from
 * the first build still arrives.
 */
export const SLUG: Record<SectionKey, string> = {
  dashboard: '', sw: 'social-work', financials: 'finance', legal: 'legal', reports: 'reports',
  property: 'property', documents: 'documents', tasks: 'tasks', time: 'time', communications: 'communications',
};
const FROM_SLUG: Record<string, SectionKey> = Object.fromEntries(
  (Object.entries(SLUG) as [SectionKey, string][]).filter(([, v]) => v).map(([k, v]) => [v, k]),
);
const OLD_SLUG: Record<string, string> = { sw: 'social-work', financials: 'finance' };

export const sectionPath = (id: string, s: SectionKey) => (SLUG[s] ? `/clients/${id}/${SLUG[s]}` : `/clients/${id}`);

export function ClientRecord() {
  const { id = '', section: rawSection, propertyId } = useParams();
  // A-#195 1: a property's own page sits in the Property section
  const raw = propertyId ? 'property' : rawSection;
  // Edit details (section 13) belongs to the Dashboard: the sidebar marks the Dashboard while it is open.
  const details = raw === 'details';
  // So does Edit people on the case (A-#97, 23 October): the people alone, from their own button.
  const people = raw === 'people';
  // The Timeline (A-#157, 27 October): open to whoever holds the Dashboard, drawn directly under it.
  const timeline = raw === 'timeline';
  const section: SectionKey = raw === undefined || details || people || timeline ? 'dashboard' : FROM_SLUG[raw] ?? (raw as SectionKey);
  const { state, search } = useLocation();
  const recorded = (state as { recorded?: string } | null)?.recorded ?? null;
  const banner = useClientBanner(id);
  const mine = useMySections();
  const me = useMe();

  if (raw !== undefined && OLD_SLUG[raw]) return <Navigate to={`/clients/${id}/${OLD_SLUG[raw]}${search}`} replace />;

  const sessionError = [me.error, banner.error].find(
    (e) => e !== null && isSessionFailure(e, { forbiddenIsSession: e === me.error }),
  );
  if (sessionError) {
    return (
      <div className="mx-auto max-w-3xl">
        <LoadFailure what="This client" error={sessionError} onRetry={() => void banner.refetch()} />
      </div>
    );
  }

  // isPending, not isLoading: see the note this replaced in the old ClientDashboard.
  if (banner.isPending) return <p className="p-6 text-neutral-600">Loading client…</p>;

  // An error takes the screen only when there is nothing to show; with data in
  // hand, Staleness reports it and the last good record stays.
  if (banner.error && !banner.data) {
    const err = banner.error;
    const status = err instanceof ApiError ? err.status : 0;
    if (status === 403 && me.isPending) return <p className="p-6 text-neutral-600">Loading client…</p>;
    if (status !== 403 && status !== 404) {
      return (
        <div className="mx-auto max-w-3xl">
          <LoadFailure what="This client" error={err} onRetry={() => void banner.refetch()}
                       retrying={banner.isFetching} forbiddenIsSession={false}
                       badLink={{ says: 'This is not a valid client link.', back: { to: '/clients', label: 'Back to the client list' } }} />
        </div>
      );
    }
    return (
      <div className="mx-auto max-w-3xl p-6">
        <h1 className="text-lg font-bold text-neutral-900">{status === 403 ? 'Not your caseload' : 'No such client'}</h1>
        <p className="mt-2 text-neutral-700">{err.message}</p>
        {err instanceof ApiError && err.hint && <p className="mt-1 text-neutral-500">{err.hint}</p>}
        <button type="button" onClick={() => void banner.refetch()}
                className="mt-5 min-h-[48px] rounded-lg bg-neutral-900 px-5 font-semibold text-white active:bg-neutral-700">
          Try again
        </button>
      </div>
    );
  }
  if (!banner.data) return null;

  const { critical } = splitByBand(banner.data);
  const held = mine.data?.sections ?? ['dashboard'];
  const shown = ORDER.filter((s) => held.includes(s));
  const allowed = held.includes(section) && (ORDER as string[]).includes(section);

  return (
    <div className="min-h-dvh bg-page">
      {/* Full-bleed, above everything, at every width, in every section. Not negotiable (CLAUDE.md). */}
      <div className="sticky top-0 z-20">
        <CriticalFlags flags={critical} />
      </div>

      <div className="mx-auto max-w-[96rem] px-0 lg:px-6 lg:py-4">
        <div className="flex flex-wrap items-center gap-x-4 px-4 pt-3 lg:px-0 lg:pt-0">
          <BackControl />
          <p className="ml-auto flex items-baseline gap-2 py-2 lg:hidden">
            <span className="font-semibold text-neutral-900">{banner.data.display_name}</span>
            <span className="font-mono text-sm text-neutral-600">{banner.data.client_ref}</span>
          </p>
        </div>

        {recorded && (
          <p role="status"
             className="mx-4 mb-3 rounded-lg border-l-8 border-note-edge bg-note-bg px-4 py-3 font-semibold text-note-fg lg:mx-0">
            {recorded}
          </p>
        )}

        {/*
          The sidebar belongs to the open client. From `lg` it is a column
          beside the section; on a tablet it is a strip of section buttons
          above it, every one 44px tall and reachable with a thumb.
        */}
        <div className="min-[900px]:grid min-[900px]:grid-cols-[12rem_minmax(0,1fr)] min-[900px]:items-start min-[900px]:gap-4">
          <nav aria-label="Sections of this client's record" className="panel min-[900px]:sticky min-[900px]:top-4">
            <p className="panel-bar hidden min-[900px]:flex">
              <span className="min-w-0 truncate">{banner.data.display_name}</span>
              <small>{banner.data.client_ref}</small>
            </p>
            <ul className="no-scrollbar flex overflow-x-auto min-[900px]:block min-[900px]:py-1.5">
              {shown.flatMap((s) => [s, ...(s === 'dashboard' ? ['timeline' as const] : [])]).map((s) => s === 'timeline' ? (
                <li key={s} className="shrink-0">
                  <NavLink to={`/clients/${id}/timeline`} end
                    className={({ isActive }) =>
                      `flex min-h-[44px] items-center gap-2 border-b-[3px] px-3 text-[0.9375rem] whitespace-nowrap min-[900px]:border-b-0 min-[900px]:border-l-[3px] ${
                        isActive
                          ? 'border-accent bg-tint font-semibold text-neutral-900'
                          : 'border-transparent font-medium text-neutral-700 hover:bg-tint'
                      }`}
                  >
                    Timeline
                  </NavLink>
                </li>
              ) : (
                <li key={s} className="shrink-0">
                  <NavLink
                    to={sectionPath(id, s)}
                    end
                    // The selected section: a tint and a marker in the accent, beside it on a
                    // wide screen and under it on the tablet strip, as in the approved layout.
                    className={({ isActive }) =>
                      `flex min-h-[44px] items-center gap-2 border-b-[3px] px-3 text-[0.9375rem] whitespace-nowrap min-[900px]:border-b-0 min-[900px]:border-l-[3px] ${
                        isActive || (s === 'dashboard' && section === 'dashboard' && !timeline) || (s === 'property' && propertyId !== undefined)
                          ? 'border-accent bg-tint font-semibold text-neutral-900'
                          : 'border-transparent font-medium text-neutral-700 hover:bg-tint'
                      }`}
                  >
                    {SECTION_LABELS[s]}
                    {RESERVED.includes(s) && (
                      <span className="ml-auto hidden text-xs font-normal text-neutral-600 min-[900px]:inline">Coming</span>
                    )}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          <main className="mt-3 min-[900px]:mt-0">
            <div className="panel mb-3">
              {/* The client bar (Arthur, 21 October, 16:12): on every view of the client, the name in bold and Refresh
                  over when it was last updated. */}
              <ClientBar name={banner.data.display_name} clientRef={banner.data.client_ref}
                         updatedAt={banner.dataUpdatedAt} isFetching={banner.isFetching} error={banner.error}
                         onRefresh={() => void banner.refetch()} />
            </div>
            {/* A-#144: a department section records an action for its own department, on this client. */}
            {mine.data && allowed && !details && !people && !timeline && (
              <SectionAction section={section} client={{ id, name: banner.data.display_name, ref: banner.data.client_ref }} />
            )}
            {!mine.data ? (
              <p className="p-6 text-neutral-600">Loading…</p>
            ) : !allowed ? (
              <SectionPanel title={SECTION_LABELS[section] ?? 'This section'}>
                <p className="px-4 py-6 text-neutral-700">
                  This part of a client’s record is not open to your roles.{' '}
                  <Link to={`/clients/${id}`} className="font-semibold underline">Back to the dashboard</Link>
                </p>
              </SectionPanel>
            ) : details ? (
              <EditDetails clientId={id} />
            ) : people ? (
              <EditPeople clientId={id} />
            ) : timeline ? (
              <TimelineSection clientId={id} sections={held} />
            ) : section === 'dashboard' ? (
              <ClientDashboardSection clientId={id} banner={banner.data} sections={held}
                                      mayRecordPeople={mine.data.may_record_people}
                                      mayRecordVisit={mine.data.may_record_visit} />
            ) : section === 'legal' ? (
              <LegalSection clientId={id} legalStatus={banner.data.legal_status} hasSw={held.includes('sw')} />
            ) : section === 'financials' ? (
              <FinancialsSection clientId={id} />
            ) : section === 'property' ? (
              propertyId
                ? <PropertyPage clientId={id} clientName={banner.data.display_name} propertyId={propertyId} />
                : <PropertySection clientId={id} />
            ) : section === 'documents' ? (
              <DocumentsSection clientId={id} />
            ) : section === 'reports' ? (
              <ReportsSection clientId={id} />
            ) : section === 'tasks' ? (
              <TasksSection clientId={id} client={{ id, name: banner.data.display_name, ref: banner.data.client_ref }} />
            ) : section === 'sw' ? (
              <SwSection clientId={id} banner={banner.data} mayRecordVisit={mine.data.may_record_visit} />
            ) : section === 'time' ? (
              <TimeSection clientId={id} />
            ) : (
              <CommunicationsSection clientId={id} />
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

/** A titled panel, the one shape every section is made of (components/Panel.tsx). */
export function SectionPanel({ title, aside, action, span = 12, children }: {
  title: string; aside?: ReactNode; action?: ReactNode; span?: 12 | 8 | 6 | 4; children: ReactNode;
}) {
  return <Panel title={title} actions={aside} action={action} span={span}>{children}</Panel>;
}
